import { SAVE_KEY, deserializeSave, serializeSave, type SaveData } from './save';

/**
 * Accès à `localStorage`, seul endroit du core à toucher une API navigateur.
 * Tout est gardé par `typeof localStorage` pour rester utilisable en Node
 * (tests Vitest, simulation headless).
 */

function storage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    // Mode privé / cookies bloqués.
    return null;
  }
}

/**
 * Résultat d'un chargement — COMMENTS v0.4.1 / arbitrage A8.
 * `reset` signale qu'une sauvegarde **existait** mais a été rejetée (version
 * antérieure à 2, ou contenu illisible) : l'écran titre l'annonce au joueur.
 */
export interface LoadOutcome {
  save: SaveData | null;
  reset: boolean;
}

/** Une sauvegarde a-t-elle été jetée au dernier `readSave()` / `loadSave()` ? */
let lastLoadWasReset = false;

/**
 * Charge la sauvegarde **et** dit si elle a été jetée.
 * La sauvegarde périmée est effacée du stockage : la partie neuve qui la
 * remplace n'a pas à rejouer le message à chaque rechargement.
 */
export function readSave(key = SAVE_KEY): LoadOutcome {
  lastLoadWasReset = false;
  const ls = storage();
  if (!ls) return { save: null, reset: false };
  let json: string | null = null;
  try {
    json = ls.getItem(key);
  } catch {
    return { save: null, reset: false };
  }
  if (!json) return { save: null, reset: false };
  const save = deserializeSave(json);
  if (save) return { save, reset: false };
  lastLoadWasReset = true;
  clearSave(key);
  return { save: null, reset: true };
}

/** La dernière lecture a-t-elle effacé une sauvegarde périmée ? */
export function wasResetOnLoad(): boolean {
  return lastLoadWasReset;
}

export function loadSave(key = SAVE_KEY): SaveData | null {
  return readSave(key).save;
}

export function writeSave(save: SaveData, key = SAVE_KEY): boolean {
  const ls = storage();
  if (!ls) return false;
  try {
    ls.setItem(key, serializeSave(save));
    return true;
  } catch {
    return false;
  }
}

export function clearSave(key = SAVE_KEY): void {
  const ls = storage();
  if (!ls) return;
  try {
    ls.removeItem(key);
  } catch {
    /* ignore */
  }
}
